import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SalesCode extends ModelObjectWithDirtyCheck<SalesCode> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Sales Code Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Sales Code Description
    * Not Nullable
    * Max Length : 1000
    */
    public description: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.description = jsonSerializedObject.description;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.description = this.description;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "SalesCode.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "description", rule: (v: any) => v == null || v.length <= 1000 || "Description has too many characters (1000)"},
    ];
}


export class SalesCodeService extends ServiceBase {

  public async getAllSalesCodes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesCode)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.salesCodes.map((v: any) => new SalesCode(v));;
    return this.makeJsonRequest("salesCode/v1/getAllSalesCodes", req, false, extractor, opts);
  };

  public async getSalesCode(salesCodeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SalesCode> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesCodeId:salesCodeId.toString(),
    };
    const extractor = (response:any) => new SalesCode(response.salesCode);;
    return this.makeJsonRequest("salesCode/v1/getSalesCode", req, false, extractor, opts);
  };

  public async getSalesCodes(salesCodeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesCode)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesCodeIds:salesCodeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.salesCodes.map((v: any) => new SalesCode(v));;
    return this.makeJsonRequest("salesCode/v1/getSalesCodes", req, false, extractor, opts);
  };

  public async saveSalesCodes(salesCodes: readonly SalesCode[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesCodes:salesCodes.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("salesCode/v1/saveSalesCodes", req, true, extractor, opts);
  };

  public async setSalesCodeIsHidden(salesCodeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesCodeId:salesCodeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("salesCode/v1/setSalesCodeIsHidden", req, true, extractor, opts);
  };
}

export const salesCodeService = new SalesCodeService();